'use client';

import { useEffect, useRef, useState } from 'react';

import styles from './ExplainerFigure.module.css';
/* Plain global sheet, not a module: figureRuntime.js writes these class names
   onto nodes it creates itself, as literal strings. See its header. */
import './figure-runtime.css';

/* One data-driven explainer figure.
 *
 * This is the studies "Ask our data" figure system ported to /science
 * explainers, with the interactivity removed. It renders the same `uhfig-*`
 * chrome (kick / title / sub / source / caption / alt) as HTML and mounts the
 * shared runtime on a `.uhfig-plot` div, which DRAWS the plot from data into
 * SVG. Because the plot is drawn from data — not a pre-baked 1600x900 SVG — the
 * runtime can redraw on a narrower, TALLER canvas for phones, wrapping labels
 * and reflowing margins so a chart stays readable in a phone column instead of
 * scaling to a 16:9 sliver.
 *
 * What is intentionally NOT here (vs. AskFigure.tsx): the "Ask our data" input
 * panel, the number/segment controls, the answer line, and the tap-away
 * dismiss handler that only mattered when a tap could pin a readout. Explainer
 * figures report; they do not answer to a reader's number.
 *
 * React renders this subtree once and never re-renders the plot mount — the
 * runtime owns everything inside `.uhfig-plot` and mutates SVG directly. The
 * mount effect re-runs (and re-draws) only when the id or the `narrow`
 * breakpoint changes, and returns the runtime's teardown.
 */

export interface ExplainerFigureData {
  /** Picks the renderer: scatter | barsV | hist | dotRows | slope | bandRows |
      rowsH | quad | ridge | lines. */
  form: string;
  /** The plot payload the chosen renderer reads. Shape is per-form — the
      authority is the renderer switch below plus utils/data/science/
      explainerFigures.json, which is the only producer of this field. */
  render: Record<string, unknown>;
}

export interface ExplainerFigureMeta {
  /** Stable id; also becomes the runtime's `data-fig` hook. */
  id: string;
  /** "Figure 1" — the small mono label above the title. */
  kick?: string | null;
  /** The finding, as a sentence. */
  title?: string | null;
  /** The numbers behind the finding. */
  sub?: string | null;
  /** "353,737 members · January–May 2026" */
  source?: string | null;
  /** Italic centred line under the plot. */
  caption?: string | null;
  /** The plot in words, for anyone who cannot see it. */
  alt?: string | null;
  /** Colour family: heart | sleep | metabolic | women | environment. Drives
      the figure accent via the CSS `data-cat` selectors. */
  cat?: string | null;
}

type MountAskFigures = (
  root: HTMLElement,
  data: Record<string, unknown>,
  opts: { narrow: boolean },
) => () => void;

const ExplainerFigure = ({
  meta,
  data,
}: {
  meta: ExplainerFigureMeta;
  data?: ExplainerFigureData;
}) => {
  const hostRef = useRef<HTMLDivElement>(null);

  /* The renderers draw once and hold no layout state, so a breakpoint change is
     a re-draw, not a reflow. Watched with matchMedia (fires twice per crossing)
     rather than a resize listener (fires per pixel). */
  const [narrow, setNarrow] = useState(false);

  /* The payload lives in a ref so the mount effect below does not depend on
     its object identity. It used to: any re-render that handed back a fresh
     `data` reference re-ran the effect, and the cleanup empties the plot the
     runtime drew into. On this page a scroll is enough to cause that — the
     chart drew, then vanished on the reader's first scroll and never came
     back, because the pending dynamic import from the previous run bails on
     its own `cancelled` guard. The payload is static per figure, so a ref is
     the honest shape for it. */
  const dataRef = useRef(data);
  dataRef.current = data;
  /* Held so the observer below can re-draw without paying for the dynamic
     import again. */
  const mountAskFiguresRef = useRef<MountAskFigures | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    const payload = dataRef.current;
    if (!host || !payload) return undefined;

    let dispose: (() => void) | undefined;
    let cancelled = false;
    let mounted = false;

    /* Loaded on demand — the runtime is large and only pages with a
       data-driven figure need it. */
    import('./figureRuntime')
      .then(({ mountAskFigures }) => {
        if (cancelled) return;
        mountAskFiguresRef.current = mountAskFigures as MountAskFigures;
        dispose = (mountAskFigures as MountAskFigures)(
          host,
          { [meta.id]: payload },
          { narrow },
        );
        mounted = true;
      })
      .catch((err) => {
        /* Loud in dev, silent for the reader: the title, sub, caption and alt
           all still describe the finding, so a failed plot degrades to a
           figure without a picture rather than a broken page. */
        if (process.env.NODE_ENV !== 'production') {
          console.error(`[explainer] figure runtime failed for ${meta.id}`, err);
        }
      });

    /* React owns .uhfig-plot's contents through dangerouslySetInnerHTML, and
       the runtime draws into that same node. Any re-render that re-creates the
       node restores the fallback paragraph and wipes the chart — on this page
       a scroll is enough, so the figure drew and then vanished the moment the
       reader moved. Watch the node and re-draw when the SVG is taken away.
       Guarded by `cancelled` so a real unmount stops it, and by the presence
       check so a redraw never stacks a second chart. */
    const host2 = host;
    const observer = new MutationObserver(() => {
      if (cancelled || !mounted) return;
      const plot = host2.querySelector('.uhfig-plot');
      if (plot && !plot.querySelector('svg')) {
        if (dispose) dispose();
        dispose = mountAskFiguresRef.current
          ? mountAskFiguresRef.current(host2, { [meta.id]: payload }, { narrow })
          : undefined;
      }
    });
    observer.observe(host2, { childList: true, subtree: true });

    return () => {
      cancelled = true;
      observer.disconnect();
      if (dispose) dispose();
    };
    /* Deliberately not `data`: see the ref above. A figure re-draws when its
       id changes or the breakpoint crosses, and at no other time. */
  }, [meta.id, narrow]);

  return (
    <figure className={styles.figure}>
      {/* No class of its own — the ex-Host wrapper's styling now lives on
          `.uhfig` in figure-runtime.css. */}
      <div ref={hostRef}>
        <div className="uhfig" data-fig={meta.id} data-cat={meta.cat || undefined}>
          <div className="uhfig-head">
            <div>
              {meta.kick ? <p className="uhfig-kick">{meta.kick}</p> : null}
              {meta.title ? <p className="uhfig-title">{meta.title}</p> : null}
              {meta.sub ? <p className="uhfig-sub">{meta.sub}</p> : null}
            </div>
          </div>

          {/* Server-rendered fallback: the plot is drawn client-side into this
              div, so on SSR / no-JS / a failed-or-racing draw it would otherwise
              be an empty hole under a caption that asserts a number. Seed it
              with the alt sentence (the finding in words); the runtime clears
              this before it draws the SVG. dangerouslySetInnerHTML keeps the
              node opaque to React so the runtime can own its children. */}
          <div
            className="uhfig-plot"
            role="img"
            aria-label={meta.alt ?? meta.title ?? undefined}
            dangerouslySetInnerHTML={{
              __html: meta.alt
                ? `<p class="uhfig-fallback">${meta.alt
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')}</p>`
                : '',
            }}
          />
          {meta.source ? <p className="uhfig-src">{meta.source}</p> : null}
        </div>
      </div>
      {meta.caption ? (
        <figcaption className={styles.caption}>{meta.caption}</figcaption>
      ) : null}
      {/* Only when it says something the title does not. On all 21 figures
          today `alt` is the title verbatim, so printing it again in 10.5px
          grey under the caption read as a duplicated line rather than as a
          description. */}
      {meta.alt && meta.alt.trim() !== (meta.title || '').trim() ? (
        <p className={styles.alt}>{meta.alt}</p>
      ) : null}
    </figure>
  );
};

export default ExplainerFigure;
